import React, { useEffect, useState } from 'react';
import { useObjectsTab, ObjectModel } from '../hooks/HObjects';
import GObjectsTable from '../../tables/GObjectsTable';
import { LoadingSpinner } from '../../utils/LoadingSpinner';
import { Pagination, Typography } from '@mui/material';

const PAGE_SIZE = 10;

export default function ObjectsTab() {
    const {
        loadPendingObjects,
        loading,
        error,
        selectedRows,
        toggleRowSelection,
        selectAllRows,
        updateObject,
        deleteObject,
    } = useObjectsTab();

    const [pendingObjects, setPendingObjects] = useState<ObjectModel[]>([]);
    const [page, setPage] = useState(1);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const results = await loadPendingObjects();
                if (mounted) setPendingObjects(results || []);
            } catch (err) {
                console.error('[ObjectsTab] loadPendingObjects failed', err);
                if (mounted) setPendingObjects([]);
            }
        })();
        return () => { mounted = false; };
    }, [loadPendingObjects]);

    const handleEditObject = async (object: ObjectModel, updatedData: Partial<ObjectModel>) => {
        try {
            await updateObject(object.id, updatedData as any);
            const refreshed = await loadPendingObjects();
            setPendingObjects(refreshed || []);
        } catch (err) {
            console.error('[ObjectsTab] edit failed', err);
        }
    };

    const handleDeleteClick = async (object: ObjectModel) => {
        try {
            await deleteObject(object);
            const refreshed = await loadPendingObjects();
            setPendingObjects(refreshed || []);
        } catch (err) {
            console.error('[ObjectsTab] delete failed', err);
        }
    };

    const handleOpenObjectDetails = (object: ObjectModel) => {
        const objectId = object?.id;
        if (objectId === null || objectId === undefined) return;
        const hash = `#objectdetails/${encodeURIComponent(String(objectId))}`;
        window.location.hash = hash;
    };

    return (
        <div className='standard-div'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className='standard-div-flex'>
                    <h1>Pending Objects</h1>
                </div>

                <div style={{ width: '100%' }}>
                    {loading ? (
                        <LoadingSpinner text='Loading pending objects...' />
                    ) : error ? (
                        <LoadingSpinner text={error} />
                    ) : pendingObjects.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                            <div style={{ fontSize: 18, marginBottom: 8 }}>No pending objects</div>
                            <div style={{ fontSize: 14 }}>There are no pending objects to review.</div>
                        </div>
                    ) : (
                        <>
                            <GObjectsTable
                                objects={pendingObjects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
                                selectedRows={selectedRows}
                                onToggleSelect={toggleRowSelection}
                                onToggleSelectAll={(select) => selectAllRows(select)}
                                onEdit={handleEditObject}
                                onDelete={handleDeleteClick}
                                user={JSON.parse(localStorage.getItem('AUTH_TOKEN') || 'null')}
                                onRowClick={handleOpenObjectDetails}
                            />

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                                <Typography variant='body2' color='text.secondary'>
                                    {pendingObjects.length} object{pendingObjects.length !== 1 ? 's' : ''}
                                </Typography>
                                {pendingObjects.length > PAGE_SIZE && (
                                    <Pagination
                                        count={Math.ceil(pendingObjects.length / PAGE_SIZE)}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        size='small'
                                        color='primary'
                                        shape='rounded'
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
